import PEOPLE from './people.json';
import POSTERS from './posters.json';
import { drawEnt, DrawEnt, Standing } from './render';

/**
 * Everyone as animated characters from the people atlases (tools/make-people.py; plan docs/plans/2026-10-17-more-people.md).
 * A body's character is cast from its id, by weight within its role, so every client agrees: protester players and
 * the crowd from the crowd cast, police from the police cast. Each body walks, runs or stands by how fast it really
 * moves, and walking frames advance by distance (per character stride), so the feet keep pace at any speed.
 */

// dress: [hue, saturation x, value x]. 0 is the protester players (--sodium, so they stand out); the rest are a muted
// dusk palette for the crowd. No saffron, no green, nothing near police khaki (brand red lines).
const DRESS: [number, number, number][] = [
  [0.1, 1.1, 1.05], [0.97, 0.55, 0.8], [0.6, 0.45, 0.75], [0.75, 0.35, 0.7], [0.52, 0.4, 0.6], [0, 0, 0.85], [0.92, 0.5, 0.6],
];
// m/s. Everyone at walking pace walks (ambient 8.8, tolis 9, the march 12.8, a player 16); a jog (24) runs
const IDLE_BELOW = 0.5, RUN_ABOVE = 19;
export const SCALE = 4.0; // people drawn over life size in a toy-scale city (docs/plans/2026-10-17-mob-streets.md; was 1.25, 2.2, 3.2)
const BUILDS_PER_FRAME = 3; // recoloured copies made per frame, so the first crowd doesn't stall a frame

// x, y, w, h, ox, oy: top-left at feet + (ox, oy). A character with a held poster (docs/plans/2026-10-18-held-posters.md)
// adds bx0, by0, bx1, by1: the carried board's own rect, same feet-relative px -- draw the poster texture straight into it.
type Frame = [number, number, number, number, number, number, number?, number?, number?, number?];
interface Anim { f: Frame[][]; stride?: number; fps?: number }
interface Char { name: string; role: 'crowd' | 'police'; height_m: number; weight: number; image: string; mask: number | null; anims: Record<'walk' | 'run' | 'idle', Anim> }
interface Track { x: number; y: number; d: number; v: number; face: number; seen: number }

const CHARS = PEOPLE.chars as unknown as Record<string, Char>;
// a character's own "-poster" entry (make-people.py, from charforge's placard prop) is a held-pose variant of it, not
// a cast of its own -- excluded here so castOf/dressOf and DRESS_OF's indexing see exactly the original cast, unchanged
const isHeldVariant = (id: string) => id.endsWith('-poster');
const ids = (role: Char['role']) => Object.keys(CHARS).filter((id) => CHARS[id].role === role && !isHeldVariant(id)).sort();
const CROWD = ids('crowd'), POLICE = ids('police');
/** two crowd dresses per crowd character, so the copies stay few: DRESS_OF[k] for the k-th crowd character */
const DRESS_OF = CROWD.map((_, k) => [1 + ((2 * k) % 6), 1 + ((2 * k + 1) % 6)]);

/** A hash of the id: the same on every client. */
const hashId = (id: number) => (id * 2654435761) >>> 0;

/** Pick from a cast by weight; characters with weight 0 are never cast. */
function pick(cast: string[], h: number): string | null {
  const total = cast.reduce((s, id) => s + CHARS[id].weight, 0);
  if (!total) return null;
  let r = ((h % 1000003) / 1000003) * total;
  for (const id of cast) if ((r -= CHARS[id].weight) < 0) return id;
  return cast.filter((id) => CHARS[id].weight > 0).at(-1)!;
}

/**
 * Story mode's friends, by their place in the toli: the two PG friends are students (plan: student-m and student-w),
 * then whoever joins. Every one of them has a look of their own, whatever its crowd weight.
 */
export const FRIENDS = ['student-m', 'student-w', 'kurta-m', 'kurti-w', 'student-m', 'teacher-m', 'student-w', 'elder-w'];

/** The character a body is drawn as: from the crowd cast for protesters (0) and npcs (2), the police cast for police (1). */
export function castOf(e: Pick<DrawEnt, 'id' | 'type' | 'k'>): string | null {
  if (e.type === 4) return FRIENDS[(e.k ?? 0) % FRIENDS.length];
  return pick(e.type === 1 ? POLICE : CROWD, hashId(e.id));
}

// ------------------------------------------------------------------ posters (docs/plans/2026-10-18-poster-bazaar.md)
interface Card { x: number; y: number; w: number; h: number; text: string }
/** the 16 posters, by slug, in the atlas's order */
export const POSTER_CARDS = POSTERS.posters as Record<string, Card>;
export const POSTER_SLUGS = Object.keys(POSTER_CARDS);
/** Of the stage's crowd, the share who carry a poster (data/story/s1e1.bazaar.json crowd.carryRatio: a feel knob). */
export const CARRY_RATIO = 0.7;
/** the held board's real width in the rig (tools/charforge/forge.js, the placard prop's default `w`) */
const RIG_BOARD_M = 0.42;

/**
 * The poster a body holds, or null. The local player holds what they took at the bazaar (`e.poster`); the stage's
 * crowd (npc rows with k 1) carry one at CARRY_RATIO, picked from the id like the cast and dress so every client agrees;
 * passers-by (k 0), police, other players and friends never do.
 */
export function posterOf(e: Pick<DrawEnt, 'id' | 'type' | 'k' | 'poster'>): string | null {
  if (e.poster !== undefined) return e.poster && POSTER_CARDS[e.poster] ? e.poster : null;
  if (e.type !== 2 || e.k !== 1) return null;
  const h = hashId(e.id ^ 0x9e3779b9); // not the cast's or the dress's bits
  if ((h % 1000) / 1000 >= CARRY_RATIO) return null;
  return POSTER_SLUGS[(h >>> 12) % POSTER_SLUGS.length];
}

/** Which dress (index into DRESS) a body wears, or -1 for fixed colours. */
export function dressOf(e: Pick<DrawEnt, 'id' | 'type' | 'k'>, id: string): number {
  if (!CHARS[id].mask) return -1;
  if (e.type === 0) return 0;
  const k = CROWD.indexOf(id);
  if (e.type === 4) return DRESS_OF[k][(e.k ?? 0) >> 2 & 1]; // the two friends dress differently from the player
  return DRESS_OF[k][(hashId(e.id) >> 8) & 1];
}

function rgb2hsv(r: number, g: number, b: number): [number, number, number] {
  const mx = Math.max(r, g, b), d = mx - Math.min(r, g, b);
  let h = 0;
  if (d) { h = (mx === r ? ((g - b) / d) % 6 : mx === g ? (b - r) / d + 2 : (r - g) / d + 4) / 6; if (h < 0) h += 1; }
  return [h, mx ? d / mx : 0, mx];
}
function hsv2rgb(h: number, s: number, v: number): number[] {
  const i = Math.floor(h * 6), f = h * 6 - i, p = v * (1 - s), q = v * (1 - f * s), t = v * (1 - (1 - f) * s);
  return [[v, t, p], [q, v, p], [p, v, t], [p, q, v], [t, p, v], [v, p, q]][i % 6];
}

export class PeopleLayer {
  private img = new Map<string, HTMLImageElement>(); // loaded atlases by character
  private sheets = new Map<string, HTMLCanvasElement>(); // `${char}:${dress}` -> the frames in that dress
  private track = new Map<number, Track>();
  private t = 0;
  private builds = 0;
  private posters: HTMLImageElement | null = null;
  /** metre: world px per metre on this map */
  constructor(private readonly metre: number) {
    for (const [id, c] of Object.entries(CHARS)) {
      if (!c.weight && !FRIENDS.includes(id)) continue; // never cast: don't fetch it
      const img = new Image();
      img.onload = () => this.img.set(id, img);
      img.src = c.image;
    }
    const p = new Image();
    p.onload = () => { this.posters = p; };
    p.src = POSTERS.image;
  }

  /** How many recoloured copies exist (the plan caps it at 28). */
  get copies() { return this.sheets.size; }

  /** The frames of character `id` in dress `k` (-1: as drawn); built on first use, the plain atlas until then. */
  private sheet(id: string, k: number): CanvasImageSource | null {
    const img = this.img.get(id);
    if (!img) return null;
    if (k < 0) return img;
    const key = id + ':' + k;
    const hit = this.sheets.get(key);
    if (hit) return hit;
    if (this.builds >= BUILDS_PER_FRAME) return img;
    this.builds++;
    const w = img.width, h = CHARS[id].mask!;
    const c = document.createElement('canvas'); c.width = w; c.height = img.height;
    const g = c.getContext('2d')!; g.drawImage(img, 0, 0);
    const base = g.getImageData(0, 0, w, h), mask = g.getImageData(0, h, w, h).data, a = base.data;
    const [hue, sm, vm] = DRESS[k];
    for (let i = 0; i < a.length; i += 4) {
      const m = mask[i + 3] / 255; if (!m) continue;
      const [, s, v] = rgb2hsv(a[i] / 255, a[i + 1] / 255, a[i + 2] / 255);
      const o = hsv2rgb(hue, Math.min(1, s * sm), Math.min(1, v * vm));
      for (let j = 0; j < 3; j++) a[i + j] += (o[j] * 255 - a[i + j]) * m;
    }
    const out = document.createElement('canvas'); out.width = w; out.height = h;
    out.getContext('2d')!.putImageData(base, 0, 0);
    this.sheets.set(key, out);
    return out;
  }

  /**
   * Everyone in view as depth-sortable items (sort them in with the trees and buildings by their feet). dt is the
   * frame time in seconds; meId gets a ring at the feet; r(e) is the body radius for the disc fallback.
   */
  standing(ents: DrawEnt[], meId: number, dt: number, z: number, r: (e: DrawEnt) => number): Standing[] {
    this.t += dt; this.builds = 0;
    const out: Standing[] = [], metre = this.metre;
    for (const e of ents) {
      const me = e.id === meId, id = castOf(e);
      if (!id) { out.push({ y: e.y, draw: (g) => drawEnt(g, e, r(e), me) }); continue; }
      const k = dressOf(e, id);
      // carrying a poster draws the held-pose variant of this same character (make-people.py's <id>-poster, from
      // charforge's placard prop) instead of the plain one, when that variant was built; else the ordinary cast
      const slug = this.posters && posterOf(e), card = slug ? POSTER_CARDS[slug] : null;
      const drawId = card && CHARS[id + '-poster'] ? id + '-poster' : id;
      const sheet = this.sheet(drawId, k);
      if (!sheet) { out.push({ y: e.y, draw: (g) => drawEnt(g, e, r(e), me) }); continue; } // until its atlas loads
      let tr = this.track.get(e.id);
      if (!tr) this.track.set(e.id, (tr = { x: e.x, y: e.y, d: 0, v: 0, face: e.face, seen: 0 }));
      let step = Math.hypot(e.x - tr.x, e.y - tr.y) / metre; // metres since the last frame
      if (step > 8) step = 0; // a respawn or a body coming into range, not a stride (a frame at 21 m/s is well under)
      if (dt > 0) tr.v += (step / dt - tr.v) * Math.min(1, dt * 8);
      tr.d += step; tr.x = e.x; tr.y = e.y; tr.seen = this.t;
      if (tr.v >= IDLE_BELOW) tr.face = e.face; // standing still keeps the last heading
      const A = CHARS[drawId].anims[tr.v < IDLE_BELOW ? 'idle' : tr.v > RUN_ABOVE ? 'run' : 'walk'];
      const dir = ((Math.round(tr.face / (Math.PI / 4)) % 8) + 8) % 8, frames = A.f[dir], n = frames.length;
      const i = A.stride ? Math.floor((tr.d / (A.stride * SCALE)) * n) % n : Math.floor(this.t * A.fps! + e.id * 0.37) % n;
      const [x, y, w, h, ox, oy, bx0, by0, bx1, by1] = frames[i];
      const u = metre * SCALE, f = u / PEOPLE.ppm;
      out.push({ y: e.y, draw: (g) => {
        // soft shadow to the east (the dusk sun is low in the west), then the local player's ring, then the body
        g.fillStyle = 'rgba(8,10,16,.35)';
        g.beginPath(); g.ellipse(e.x + 0.22 * u, e.y, 0.42 * u, 0.17 * u, 0, 0, Math.PI * 2); g.fill();
        if (me) {
          g.strokeStyle = 'rgba(255,255,255,.85)'; g.lineWidth = 1.5 / z;
          g.beginPath(); g.ellipse(e.x, e.y, 0.55 * u, 0.24 * u, 0, 0, Math.PI * 2); g.stroke();
        }
        g.drawImage(sheet, x, y, w, h, e.x + ox * f, e.y + oy * f, w * f, h * f);
        // the board is baked into the held-pose rig (charforge's placard prop); bx0..by1 is its exact screen rect for
        // this frame, feet-relative like ox,oy. The rig's board is a real 0.42 m, too small to read at play zoom, so the
        // poster is drawn at posters.json's widthM instead, in its own aspect, standing on the board's bottom edge (the
        // grip): it still travels with the hand frame by frame, and covers the rig's plain board
        if (card && bx0 !== undefined) {
          const bw = (bx1! - bx0) * f, pw = bw * (POSTERS.widthM / RIG_BOARD_M), ph = (pw * card.h) / card.w;
          const cx = e.x + ((bx0 + bx1!) / 2) * f, foot = e.y + by1! * f;
          g.drawImage(this.posters!, card.x, card.y, card.w, card.h, cx - pw / 2, foot - Math.max(ph, (by1! - by0!) * f), pw, Math.max(ph, (by1! - by0!) * f));
        }
      } });
    }
    for (const [id, tr] of this.track) if (this.t - tr.seen > 2) this.track.delete(id);
    return out;
  }

  /** Height of a body's crown above its feet in world px, for placing names and messages over heads. */
  heightOf(e: Pick<DrawEnt, 'id' | 'type' | 'k'>): number {
    const id = castOf(e);
    return (id ? CHARS[id].height_m : 1.75) * SCALE * this.metre;
  }
}
